import { FunctionComponent, ReactNode } from "react";

export type Command = {
  id: string;
  title: string;
  icon?: ReactNode;
  Component?: FunctionComponent<LaunchProps>;
  onSelect?: () => void;
};

export type RichSearchResult = {
  title: string;
  when: (searchText: string) => boolean;
  result: (searchText: string) => Promise<string>;
  actions: (searchText: string) => Action[];
};

export type ActionModifier = "shift" | "alt" | "ctrl" | "meta";

export type Action = {
  title: string;
  /** When set, this action runs only while the given modifier key is held (e.g. Shift+Enter). */
  modifier?: ActionModifier;
  onAction?: (options: ActionOptions) => void;
};

export type ActionOptions = {
  close: () => void;
};

/** A subset of KeyboardEvent/MouseEvent — both expose these flags, so an event can be passed directly. */
export type ModifierState = {
  shiftKey: boolean;
  altKey: boolean;
  ctrlKey: boolean;
  metaKey: boolean;
};

const MODIFIER_FLAG: Record<ActionModifier, keyof ModifierState> = {
  shift: "shiftKey",
  alt: "altKey",
  ctrl: "ctrlKey",
  meta: "metaKey",
};

export function isModifierHeld(modifier: ActionModifier, state: ModifierState) {
  return state[MODIFIER_FLAG[modifier]];
}

/**
 * Picks the action whose modifier is currently held, falling back to the
 * unmodified action. Selection is keyed on the modifier, not on array position.
 */
export function resolveAction(actions: Action[], state: ModifierState): Action | undefined {
  const modified = actions.find(action => action.modifier && isModifierHeld(action.modifier, state));
  return modified ?? actions.find(action => !action.modifier) ?? actions[0];
}

export type LaunchProps = {
  fallbackText?: string;
};
